import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';

/**
 * Socle commun des exports Excel (comptabilité, factures, encaissements) :
 * dates et montants sont de vrais nombres formatés (triables, filtrables), les totaux sont des formules,
 * chaque tableau a un filtre automatique et des colonnes à la largeur de leur contenu.
 */
export const DATE_FORMAT = 'dd/mm/yyyy';
export const MONEY_FORMAT = '#,##0.00';
export const INTEGER_FORMAT = '0';
const MAX_COLUMN_WIDTH = 60;

/** Cellule Excel typée : nombre (avec son format), texte, ou vide */
export function cell(value, format) {
    if (value === undefined || value === null || value === '') return null;
    return typeof value === 'number' ? {t: 'n', v: value, z: format} : {t: 's', v: String(value)};
}

/** Cellule de total : formule SUM (valeur calculée mémorisée pour les lecteurs qui ne recalculent pas) */
export function sumCell(columnIndex, firstRow, lastRow, value) {
    const column = XLSX.utils.encode_col(columnIndex);
    return {t: 'n', v: value, f: `SUM(${column}${firstRow}:${column}${lastRow})`, z: MONEY_FORMAT};
}

export const round2 = value => Math.round((value + Number.EPSILON) * 100) / 100;
export const total = (rows, pick) => round2(rows.reduce((sum, row) => sum + (pick(row) || 0), 0));

/** Largeur d'une colonne : celle de son contenu, bornée */
function columnWidths(aoa, columnCount) {
    return Array.from({length: columnCount}, (_, index) => ({
        wch: Math.min(MAX_COLUMN_WIDTH, Math.max(8, ...aoa.map(row => {
            const content = row[index];
            const label = content && typeof content === 'object' ? (content.t === 'n' ? (content.z === DATE_FORMAT ? DATE_FORMAT : content.v.toFixed(2)) : content.v) : content;
            return label === undefined || label === null ? 0 : String(label).length;
        })) + 2)
    }));
}

/**
 * Feuille « tableau » : en-têtes, une ligne par élément, filtre automatique, et une ligne de totaux (formules) après une ligne vide.
 * `columns` : [{header, pick(row), format, total}]
 */
export function addTable(workbook, name, columns, rows) {
    const aoa = [columns.map(column => column.header)];
    rows.forEach(row => aoa.push(columns.map(column => cell(column.pick(row), column.format))));

    const lastRow = rows.length + 1;
    if (rows.length > 0 && columns.some(column => column.total)) {
        aoa.push([]);
        aoa.push(columns.map((column, index) => index === 0 ? {t: 's', v: 'TOTAL'}
            : (column.total ? sumCell(index, 2, lastRow, total(rows, row => column.pick(row))) : null)));
    }

    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    worksheet['!cols'] = columnWidths(aoa, columns.length);
    worksheet['!autofilter'] = {ref: `A1:${XLSX.utils.encode_col(columns.length - 1)}${Math.max(lastRow, 2)}`};
    XLSX.utils.book_append_sheet(workbook, worksheet, name);
}

/** Feuille libre, construite ligne à ligne */
export function addFreeSheet(workbook, name, aoa, columnCount) {
    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    worksheet['!cols'] = columnWidths(aoa, columnCount);
    XLSX.utils.book_append_sheet(workbook, worksheet, name);
}

export const text = value => ({t: 's', v: String(value)});

/** Classeur d'une seule feuille « tableau » (voir addTable) */
export function singleTableWorkbook(sheetName, columns, rows) {
    const workbook = XLSX.utils.book_new();
    addTable(workbook, sheetName, columns, rows);
    return workbook;
}

export function downloadWorkbook(workbook, fileName) {
    const buffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array', compression: true, bookSST: true});
    saveAs(new Blob([buffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}), fileName);
}
